import { CSSProperties, useState } from "react";
import { useTranslation } from "react-i18next";
import CachedIcon from "@mui/icons-material/Cached";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import { AssetDetailsDTO, AssetKey } from "../../../dto/AssetDTO";
import { useAssetPreview } from "../../../hooks/AssetPreviewHook";
import FileExplorerDialog from "../../file_explorer/FileExplorerDialog";
import { PowerSlideAssetSlot as AssetSlotDefinition } from "../../../models/interactions/PowerSlideTemplates";
import {
  powerSlideMedia,
  powerSlideRadius,
  powerSlideType,
} from "./PowerSlideDesign";
import { PowerSlideSurfaceStyle } from "./PowerSlideSurfaces";

interface PowerSlideAssetSlotProps {
  slot: AssetSlotDefinition;
  assetId: string;
  surface: PowerSlideSurfaceStyle;
  onChange: (assetId: string) => void;
  /** Full bleed slots fill the slide and draw no border of their own. */
  fullBleed?: boolean;
  /** Extra chrome drawn over a filled video slot. */
  children?: React.ReactNode;
  style?: CSSProperties;
  /** No prompt, no controls, no picker - for the rail's miniatures. */
  readOnly?: boolean;
}

/**
 * An asset slot. Empty, it is a dashed accent border over a near-neutral fill;
 * filled, the dashed border is removed because nothing is awaiting input any
 * more. Text inputs use a solid fill and a solid grey border instead, so the
 * two input kinds differ in shape as well as colour and survive greyscale.
 */
export default function PowerSlideAssetSlot(props: PowerSlideAssetSlotProps) {
  const {
    slot,
    assetId,
    surface,
    onChange,
    fullBleed,
    children,
    style,
    readOnly,
  } = props;
  const { t } = useTranslation();
  const [isPickerOpen, setPickerOpen] = useState(false);
  const { getAssetPreview } = useAssetPreview(assetId || undefined);
  const previewUrl = assetId ? getAssetPreview() : undefined;
  const isVideo = slot.kind === "video";
  const isEmpty = !assetId;

  const onSelect = (asset: AssetDetailsDTO) => {
    onChange(asset.assetId);
    setPickerOpen(false);
  };

  const controlButton = (
    label: string,
    icon: React.ReactNode,
    onClick: () => void
  ) => (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: powerSlideMedia.controlSize,
        height: powerSlideMedia.controlSize,
        borderRadius: powerSlideRadius.pill,
        backgroundColor: "#ffffff",
        color: "#272d37",
        boxShadow: "0px 1px 4px 0px rgba(0, 0, 0, 0.18)",
      }}
    >
      {icon}
    </button>
  );

  const emptyPrompt = (
    <button
      type="button"
      aria-label={`powerslide-upload-${slot.id}`}
      onClick={() => setPickerOpen(true)}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: powerSlideMedia.promptGap,
        width: "100%",
        height: "100%",
        color: surface.accent,
        ...powerSlideType.uiLabel,
      }}
    >
      {isVideo ? (
        <VideocamOutlinedIcon
          style={{
            width: powerSlideMedia.glyphWidth,
            height: powerSlideMedia.glyphHeight,
          }}
        />
      ) : (
        <ImageOutlinedIcon
          style={{
            width: powerSlideMedia.glyphWidth,
            height: powerSlideMedia.glyphHeight,
          }}
        />
      )}
      {isVideo
        ? t("powerslide.slot.uploadVideo", { defaultValue: "Click to upload video" })
        : t("powerslide.slot.uploadImage", { defaultValue: "Click to upload image" })}
    </button>
  );

  return (
    <>
      <div
        data-testid={`powerslide-slot-${slot.id}`}
        style={{
          position: "relative",
          overflow: "hidden",
          borderRadius: fullBleed ? 0 : powerSlideRadius.lg,
          backgroundColor: isEmpty
            ? surface.dropZoneFill
            : isVideo
              ? "#1c2a37"
              : surface.slotFill,
          // Dashed marks "awaiting an asset". Once filled there is nothing to
          // await, so the border goes and the image sits flush. A full-bleed
          // slot has no inset to draw one in, so it relies on the prompt.
          border:
            isEmpty && !fullBleed && !readOnly
              ? `${powerSlideMedia.emptyBorderWidth}px dashed ${surface.accent}`
              : undefined,
          boxSizing: "border-box",
          ...style,
        }}
      >
        {isEmpty ? (
          readOnly ? null : (
            emptyPrompt
          )
        ) : (
          <>
            {previewUrl && (
              <img
                src={previewUrl}
                alt=""
                style={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  // Contained, so an author always sees the whole asset they
                  // chose rather than a crop of it. Overlay Image is the one
                  // exception: it is the slide's ground, and letterboxing a
                  // full-bleed background would defeat the template.
                  objectFit: fullBleed ? "cover" : "contain",
                }}
              />
            )}
            {children}
            <div
              hidden={readOnly}
              style={{
                position: "absolute",
                top: powerSlideMedia.controlInset,
                right: powerSlideMedia.controlInset,
                display: readOnly ? "none" : "flex",
                gap: powerSlideMedia.controlGap,
              }}
            >
              {controlButton(
                `powerslide-replace-${slot.id}`,
                <CachedIcon
                  style={{
                    width: powerSlideMedia.controlIconSize,
                    height: powerSlideMedia.controlIconSize,
                  }}
                />,
                () => setPickerOpen(true)
              )}
              {controlButton(
                `powerslide-remove-${slot.id}`,
                <DeleteOutlineIcon
                  style={{
                    width: powerSlideMedia.controlIconSize,
                    height: powerSlideMedia.controlIconSize,
                  }}
                />,
                () => onChange("")
              )}
            </div>
          </>
        )}
      </div>

      <FileExplorerDialog
        open={isPickerOpen}
        onClose={() => setPickerOpen(false)}
        type={isVideo ? AssetKey.Video : AssetKey.Image}
        onSelect={onSelect}
      />
    </>
  );
}

/** The play button and duration badge that make a video slot read as video. */
export function PowerSlideVideoChrome() {
  return (
    <>
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          left: "50%",
          top: "50%",
          transform: "translate(-50%, -50%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: powerSlideMedia.playButtonSize,
          height: powerSlideMedia.playButtonSize,
          borderRadius: powerSlideRadius.pill,
          backgroundColor: "rgba(255, 255, 255, 0.94)",
          color: "#1c2a37",
          boxShadow: "0px 2px 8px 0px rgba(0, 0, 0, 0.2)",
        }}
      >
        <PlayArrowIcon
          style={{
            width: powerSlideMedia.playGlyphWidth + 12,
            height: powerSlideMedia.playGlyphHeight + 12,
          }}
        />
      </div>
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          right: powerSlideMedia.durationInset,
          bottom: powerSlideMedia.durationInset,
          paddingInline: powerSlideMedia.durationPaddingInline,
          paddingBlock: powerSlideMedia.durationPaddingBlock,
          borderRadius: powerSlideRadius.sm,
          backgroundColor: "rgba(28, 42, 55, 0.78)",
          color: "#ffffff",
          ...powerSlideType.uiLabel,
        }}
      >
        {/* The real duration comes from the asset at runtime. */}
        0:00
      </div>
    </>
  );
}
